import { type AppState as Auth0AppState, Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { type ReactNode, createContext, useCallback, useContext, useMemo } from 'react';
import type { TokenGetter } from '../api/client';
import { env, hasAuth0 } from '../env';
import { useDevTokenStore } from '../stores/devToken';

export interface AuthUser {
  name?: string;
  email?: string;
  sub?: string;
}

export interface NormalizedAuth {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: AuthUser | null;
  signIn: () => void;
  signOut: () => void;
  getToken: TokenGetter;
}

const AuthCtx = createContext<NormalizedAuth | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  if (hasAuth0()) {
    return (
      <Auth0Provider
        domain={env.AUTH0_DOMAIN as string}
        clientId={env.AUTH0_CLIENT_ID as string}
        authorizationParams={{
          redirect_uri: window.location.origin,
          audience: env.AUTH0_AUDIENCE,
          scope: 'openid profile email offline_access',
        }}
        useRefreshTokens
        // Persist the token across page reloads. With "memory" (the safer
        // default for hostile-XSS environments) any reload forces the user
        // back through the Auth0 redirect. For a tester deploy with our
        // own SPA + tight CSP, localstorage is the right tradeoff.
        cacheLocation="localstorage"
        onRedirectCallback={onRedirectCallback}
      >
        <Auth0Bridge>{children}</Auth0Bridge>
      </Auth0Provider>
    );
  }
  return <DevAuthBridge>{children}</DevAuthBridge>;
}

function onRedirectCallback(appState?: Auth0AppState) {
  window.history.replaceState({}, document.title, appState?.returnTo ?? window.location.pathname);
}

function Auth0Bridge({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated, user, loginWithRedirect, logout, getAccessTokenSilently } =
    useAuth0();

  const getToken: TokenGetter = useCallback(async () => {
    if (!isAuthenticated) return null;
    return getAccessTokenSilently();
  }, [isAuthenticated, getAccessTokenSilently]);

  const value = useMemo<NormalizedAuth>(
    () => ({
      isLoading,
      isAuthenticated,
      user: user
        ? {
            ...(user.name !== undefined ? { name: user.name } : {}),
            ...(user.email !== undefined ? { email: user.email } : {}),
            ...(user.sub !== undefined ? { sub: user.sub } : {}),
          }
        : null,
      signIn: () => loginWithRedirect(),
      signOut: () => logout({ logoutParams: { returnTo: window.location.origin } }),
      getToken,
    }),
    [isLoading, isAuthenticated, user, loginWithRedirect, logout, getToken],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

function DevAuthBridge({ children }: { children: ReactNode }) {
  const devToken = useDevTokenStore((s) => s.token);
  const isAuthenticated = env.AUTH0_DEV_BYPASS && !!devToken;
  const getToken: TokenGetter = useCallback(async () => {
    return isAuthenticated ? (devToken as string) : null;
  }, [isAuthenticated, devToken]);

  const value = useMemo<NormalizedAuth>(
    () => ({
      isLoading: false,
      isAuthenticated,
      user: isAuthenticated
        ? { name: 'Dev User', email: 'dev@aiassist.app', sub: 'auth0|dev-bypass' }
        : null,
      signIn: () => {
        // eslint-disable-next-line no-alert
        alert(
          'Auth0 not configured. Set VITE_AUTH0_DOMAIN + VITE_AUTH0_CLIENT_ID, or VITE_AUTH0_DEV_BYPASS=1 + VITE_DEV_JWT for local dev.',
        );
      },
      signOut: () => {},
      getToken,
    }),
    [isAuthenticated, getToken],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): NormalizedAuth {
  const ctx = useContext(AuthCtx);
  if (!ctx) {
    throw new Error('useAuth must be used within <AuthProvider>');
  }
  return ctx;
}
